<script>
import { mapState, mapActions } from 'vuex';
import api from 'src/api';
import { eventBus } from 'src/features';
import { toast, isEmpty } from 'src/utils';
import i18n from 'src/features/i18n';

export default {
  props: ['value'],
  data() {
    return {
      searchString: null,
      emptySearchResult: false,
      loadingErr: false,
    };
  },
  async mounted() {
    await this.loadUcceAgents({ startIndex: 0, pageSize: this.ucceAgentsPageSizeDefault })
      .catch(() => {
        this.loadingErr = true;
      });
  },
  computed: {
    ...mapState('users', ['ucceAgentsTotal', 'ucceAgents',
      'ucceAgentsFetching', 'ucceAgentsPageSizeDefault']),
    chosenUcceAgent() {
      return `${this.value.lastName} ${this.value.firstName}`;
    },
    searchDisabled() {
      return isEmpty(this.searchString);
    },
    table() {
      return {
        key: 'agentId',
        cols: [{
          name: 'lastName',
          label: this.$t('T_LAST_NAME'),
          type: String,
        }, {
          name: 'firstName',
          label: this.$t('T_FIRST_NAME'),
          type: String,
        }, {
          name: 'agentId',
          label: this.$t('T_AGENT_ID'),
          type: String,
        }, {
          name: 'userName',
          label: this.$t('T_AGENT_USERNAME'),
          type: String,
        }, {
          name: 'action',
          label: this.$t('T_ACTIONS'),
          classHeader: this.$style.tableLast,
          classData: this.$style.tableLast,
        }],
        rows: this.ucceAgents,
      };
    },
  },
  methods: {
    ...mapActions('users', ['loadUcceAgents']),
    moreResults({ page, pageSize }) {
      const startIndex = (page - 1) * pageSize;
      this.loadUcceAgents({
        startIndex,
        moreData: true,
        pageSize,
        search: this.searchString,
      })
        .catch(() => {
          this.loadingErr = true;
        });
    },
    refreshPage(pageSize) {
      this.loadingErr = false;
      this.loadUcceAgents({ startIndex: 0, pageSize, search: this.searchString })
        .catch(() => {
          this.loadingErr = true;
        });
    },
    async selectUcceAgent(agent) {
      const agentData = await api.checkUserUniquenessUccePeripheral(agent.agentId);
      if (!agentData.used) {
        this.$parent.close([null]);
        eventBus.$emit('selectUcceAgent', agent);
      } else {
        const msg = `${i18n.t('T_UCCE_AGENT_IS_BINDED_TO_ANOTHER_USER')} - `
          + `${agentData.firstName} ${agentData.lastName}`;
        toast.error({
          msg,
        });
      }
    },
    close() {
      this.$parent.close([null]);
    },
    searchUcceAgent() {
      this.emptySearchResult = false;
      this.loadingErr = false;
      this.loadUcceAgents({ startIndex: null, pageSize: this.ucceAgentsPageSizeDefault, search: this.searchString })
        .then(() => {
          if (isEmpty(this.ucceAgents)) {
            this.emptySearchResult = true;
          }
        })
        .catch(() => {
          this.loadingErr = true;
        });
    },
    emptySearch() {
      this.emptySearchResult = false;
      this.searchString = null;
      this.loadingErr = false;
      this.loadUcceAgents({ startIndex: 0, pageSize: this.ucceAgentsPageSizeDefault })
        .catch(() => {
          this.loadingErr = true;
        });
    },
    selectedRow(data) {
      return data.agentId === this.value.agentId;
    },
  },
};
</script>

<template lang="pug">
div(:class="$style.root")
  header(:class="$style.header")
    div(:class="$style.headerTitle") {{ value.header }}
    div(:class="$style.icon", @click="close",
          tabindex="0", @keyup.enter="close")
          ui-icon(:class="$style.close", key="close", title="Закрыть" data-test="CLOSE_BUTTON") close
  div(:class="$style.content")
    div(:class="$style.searchWrapper")
      v-text-field(:label="$t('T_SEARCH_AGENT')", v-model="searchString")
      ui-button(colored="primary", :class="$style.searchBtn", @click.native="searchUcceAgent",
        @keyup.enter.native="searchUcceAgent", :disabled="searchDisabled") {{$t('T_SEARCH')}}
      ui-button(colored="lightAccent", :class="$style.searchBtn", @click.native="emptySearch",
        @keyup.enter.native="emptySearch") {{$t('T_EMPTY_FILTER')}}
    div(v-if="value.agentId", :class="$style.agentSelectedWrapper")
      ui-icon(:class="$style.iconSelected") check_box
      div {{$t('T_CHOSEN_AGENT')}} - {{ chosenUcceAgent }}
    div
      ui-progress(:class="$style.loading" v-if="ucceAgentsFetching")
      ui-table(
        v-if="ucceAgents && ucceAgents.length && ucceAgentsPageSizeDefault"
        :data="table",
        :class="$style.table"
        @moreResults="moreResults"
        :pageCustom="true"
        @refreshPage="refreshPage"
        :totalCount="ucceAgentsTotal"
        :pageSizeDefault="ucceAgentsPageSizeDefault"
        :fetching="ucceAgentsFetching"
        :moreButtonText="$t('T_MORE_AGENTS')")
        template(slot="action", slot-scope="{ data }")
          div(:class="$style.actions")
            ui-button(
              :disabled="selectedRow(data)"
              :icon="selectedRow(data) ? 'check_box' : 'check_box_outline_blank'",
              type="icon",
              @click.native="selectUcceAgent(data)",
              @keyup.enter.native="$router.push({ name: 'edit_user', params: { id: data.id } })")

    div(v-if="emptySearchResult" :class="$style.subHeader") {{ $t('T_NO_RESULTS') }}
    div(v-if="loadingErr" :class="$style.subHeaderRed") {{ $t('T_ERROR_LOADING_UCCE_AGENTS') }}
</template>

<style lang="sass" module>
.root
  display: block
  max-height: 800px
  width: 1100px
.header
  display: flex
  flex-grow: 1
  justify-content: center
  align-items: center
  padding: 24px
  border-bottom: 1px solid $color_divider
  &Title
    font-size: 18px
    display: flex
    justify-content: center
    align-items: center
    font-size: 18px
    font-weight: 400
    padding-bottom: 2px
    margin-left: 12px
    flex-grow: 1
.content
  padding: 20px 40px
.searchWrapper
  display: flex
  align-items: center
.searchBtn
  margin: 0 0 24px 30px
.tableWrapper
  position: relative
.table
  position: relative
  margin-top: 20px
.loading
  position: absolute
  left: 0
  right: 0
  bottom: 0
.subHeader
  padding: 24px 0
  &Red
    padding: 24px 0
    color: red
.agentSelectedWrapper
  color: $color_primary
  display: flex
.iconSelected
  width: 20px
  height: 20px
  margin-right: 10px
.icon
  width: 36px
  height: 36px
  display: flex
  align-items: center
  justify-content: center
  border-radius: 50%
  cursor: pointer
  &:hover
    background: $color_main_light_third
    color: $color_msg_dark
  &:focus
    background: $color_main_light_third
    color: $color_msg_dark
    outline: none
  .close
    width: 70%

</style>
